import { useQueryClient } from '@tanstack/react-query'
import { RootState } from 'client/store'
import filesSelectors from 'client/store/files/filesSelectors'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { SeoDto } from 'src/server/modules/page/shared/dto/base-content.dto'
import { EditorPageDto } from 'src/server/modules/page/shared/dto/page-content.dto'
import { ContentEntitiesDto } from 'src/server/modules/page/shared/dto/website-content.dto'
import { WebsitePageDataInterface } from 'src/shared/types/website-settings-types'
import {
  WebsitePagesContentCache,
  websitePageContentQuery,
} from '../api/queries/website-page-content-query'
import { getWebsitePageById } from '../api/website-pages-api'
import { getWebsiteLayoutPageIdForLocale } from '../shared/utils/get-website-layout-page-id'

export const useWebsitePagesContent = () => {
  const queryClient = useQueryClient()
  const websitePageContentQueryKey = websitePageContentQuery.getQueryKey()
  const pageState = useSelector((state: RootState) => state.page.present)
  const filesState = useSelector((state: RootState) => state.files)

  /**
   * Syncs the cache with current Redux state for a specific page.
   * Call this after saving to update cache with the latest edited content.
   */
  const syncCacheFromRedux = useCallback(
    (pageId: number) => {
      const pagesCache =
        queryClient.getQueryData<WebsitePagesContentCache>(
          websitePageContentQueryKey,
        ) ?? {}
      const cachedPage = pagesCache[pageId]

      if (!cachedPage) {
        return
      }

      // Build updated EditorPageDto from current Redux state
      // Type assertion needed because Redux types are more flexible than DTO types
      const updatedPageContent: EditorPageDto = {
        page: {
          ...cachedPage.page,
          content: {
            entities: pageState.entities as ContentEntitiesDto,
            globalSettings: pageState.globalSettings,
            seo: pageState.seo as SeoDto,
            tracking: pageState.tracking,
          },
        },
        files: filesSelectors.getAllFiles(filesState),
        colors: cachedPage.colors,
      }

      queryClient.setQueryData<WebsitePagesContentCache>(
        websitePageContentQueryKey,
        oldData => ({
          ...oldData,
          [pageId]: updatedPageContent,
        }),
      )
    },
    [queryClient, websitePageContentQueryKey, pageState, filesState],
  )

  const getWebsitePageContent = useCallback(
    async (pageId: number, providedLayoutPageId?: number) => {
      // Check cache first
      const pagesCache =
        queryClient.getQueryData<WebsitePagesContentCache>(
          websitePageContentQueryKey,
        ) ?? {}
      const cachedPage = pagesCache[pageId]

      if (cachedPage) {
        return { pageContent: cachedPage, isFromCache: true }
      }

      let websiteData: WebsitePageDataInterface | undefined
      let layoutPageId = providedLayoutPageId

      // Fetch website data to get layoutPageId only if not provided
      if (!layoutPageId) {
        websiteData = await getWebsitePageById(pageId)
        layoutPageId = getWebsiteLayoutPageIdForLocale({
          layouts: websiteData.layouts,
          currentLocale: websiteData.locale,
        })
      }

      const pageContent = await websitePageContentQuery.queryFn(
        pageId,
        layoutPageId,
      )

      queryClient.setQueryData<WebsitePagesContentCache>(
        websitePageContentQueryKey,
        oldData => ({
          ...oldData,
          [pageId]: pageContent,
        }),
      )

      return {
        pageContent,
        websiteData,
        layoutPageId,
        isFromCache: false,
      }
    },
    [queryClient, websitePageContentQueryKey],
  )

  return {
    getWebsitePageContent,
    syncCacheFromRedux,
  }
}
